import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@mui/material";
import { ArrowLeft } from "lucide-react";
import { ContentCatalogService } from "@services/contentCatalog/ContentCatalogService";
import { StoreService } from "@services/store/StoreService";
import type { StoreCollectionPage } from "@services/store/commands/cmdGetStoreCollection";
import { OrganizationService } from "@services/organizations/OrganizationService";
import { CoreService } from "@services/core/CoreService";
import type { TagGroup } from "@models/tagGroup.model";
import { ContentCatalogFolder } from "@models/contentCatalogFolder.model";
import { Ad } from "@models/Ad.model";
import AdCard from "@/shared/components/AdCard/AdCard";
import { Paths } from "@/paths";
import { stripHtmlTags } from "@/shared/utils/common";
import StoreHero from "./StoreHero";
import FolderAccordion from "./FolderAccordion";
import CollectionSelector from "./CollectionSelector";
import StoreFilterBar from "./StoreFilterBar";
import { filterAds, filterFolders, isFiltering, storeFilterGroups, type AdTagMap } from "./storeFilter";
import { motiStoreCache } from "./cache";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import "./motiStore.scss";

function storeCollectionToFolder(node: StoreCollectionPage): ContentCatalogFolder {
    const folder = new ContentCatalogFolder();
    folder.id = node.collection_id;
    folder.name = node.collection_name;
    folder.ads = (node.ads ?? []).map((ad) => new Ad(ad));
    folder.children = (node.children ?? []).map(storeCollectionToFolder);
    return folder;
}

export default function CollectionStorePage() {
    const { collectionId, tagId } = useParams();
    const cached = collectionId
        ? motiStoreCache.getCollection(collectionId)
        : tagId
            ? motiStoreCache.getTag(tagId)
            : null;

    const [rootAds, setRootAds] = useState<Ad[]>(cached?.rootAds ?? []);
    const [folders, setFolders] = useState<ContentCatalogFolder[]>(cached?.folders ?? []);
    const [heroSrc, setHeroSrc] = useState(cached?.heroSrc ?? "");
    const [title, setTitle] = useState(cached?.title ?? "");
    const [copyText, setCopyText] = useState(cached?.copyText ?? "");
    const [adTags, setAdTags] = useState<AdTagMap>(cached?.adTags ?? {});
    const [isLoaded, setIsLoaded] = useState(!!cached);
    const [selectorOpen, setSelectorOpen] = useState(false);

    const [tagGroups, setTagGroups] = useState<TagGroup[]>([]);
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
    const [query, setQuery] = useState("");

    useDocumentTitle(title || "Moti Store");

    // A different collection is a different set of ads, so old selections would silently
    // filter it to nothing.
    useEffect(() => {
        setSelectedTagIds([]);
        setQuery("");
    }, [collectionId, tagId]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const defaultOrg = await new OrganizationService().getDefaultOrganization();
                if (!defaultOrg || cancelled) return;
                const orgId = defaultOrg.id;

                // The full taxonomy, from the endpoint the Content Catalog reads (MPN-1575).
                // A group added there is filterable here with no change on this side. Fetched
                // even when the page itself is cached, since the cache holds ads and not tags,
                // and caught separately so a failure costs the filter and not the page.
                new CoreService().getTagGroups()
                    .then((groups) => { if (!cancelled) setTagGroups(storeFilterGroups(groups ?? [])); })
                    .catch((err) => console.error("error loading tag groups", err));

                if (cached) return;

                if (collectionId) {
                    const response = await new StoreService().getCollection(
                        String(orgId),
                        collectionId,
                        "Home",
                    );
                    if (response && !cancelled) {
                        const rootAds = (response.ads ?? []).map((ad) => new Ad(ad));
                        const folders = (response.children ?? []).map(storeCollectionToFolder);
                        const title = response.collection_name ?? "";
                        const copyText = response.copy_text ?? "";
                        const heroSrc = response.hero ?? "";
                        const adTags = response.ad_tags ?? {};
                        setTitle(title);
                        setCopyText(copyText);
                        setHeroSrc(heroSrc);
                        setRootAds(rootAds);
                        setFolders(folders);
                        setAdTags(adTags);
                        motiStoreCache.setCollection(collectionId, {
                            title, copyText, heroSrc, rootAds, folders, adTags,
                        });
                    }
                } else if (tagId) {
                    const response = await new ContentCatalogService().getTagCollection(String(orgId), tagId);
                    if (response && !cancelled) {
                        const wrapper = new ContentCatalogFolder();
                        wrapper.id = response.tag_id;
                        wrapper.name = response.tag_name;
                        wrapper.children = [];
                        wrapper.ads = (response.ads ?? []).map((ad) => new Ad(ad));
                        const folders = [wrapper];
                        const title = response.tag_name ?? "";
                        const copyText = response.copy_text ?? "";
                        const heroSrc = response.hero ?? "";
                        const adTags = response.ad_tags ?? {};
                        setRootAds([]);
                        setFolders(folders);
                        setTitle(title);
                        setCopyText(copyText);
                        setHeroSrc(heroSrc);
                        setAdTags(adTags);
                        motiStoreCache.setTag(tagId, {
                            title, copyText, heroSrc, rootAds: [], folders, adTags,
                        });
                    }
                }
            } catch (err) {
                console.error("error loading collection/tag", err);
            } finally {
                if (!cancelled) setIsLoaded(true);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [collectionId, tagId]);

    const filtering = isFiltering(selectedTagIds, query);
    const visibleRootAds = useMemo(
        () => filterAds(rootAds, selectedTagIds, query, adTags),
        [rootAds, selectedTagIds, query, adTags],
    );
    const visibleFolders = useMemo(
        () => filterFolders(folders, selectedTagIds, query, adTags),
        [folders, selectedTagIds, query, adTags],
    );
    const visibleCount = useMemo(() => {
        const countAds = (folder: ContentCatalogFolder): number =>
            (folder.ads?.length ?? 0) + (folder.children ?? []).reduce((n, c) => n + countAds(c), 0);
        return visibleRootAds.length + visibleFolders.reduce((n, f) => n + countAds(f), 0);
    }, [visibleRootAds, visibleFolders]);

    // Named `id`, not `tagId`: that name is already the route param for a tag page.
    const toggleTag = (id: number) =>
        setSelectedTagIds((prev) =>
            prev.includes(id) ? prev.filter((selected) => selected !== id) : [...prev, id],
        );

    const selectorFolders = useMemo(() => {
        if (!collectionId) return folders;
        const synthetic = new ContentCatalogFolder();
        synthetic.id = Number(collectionId);
        synthetic.name = title;
        synthetic.ads = rootAds;
        synthetic.children = folders;
        return [synthetic];
    }, [collectionId, title, rootAds, folders]);

    if (!isLoaded) return null;

    const isEmpty = rootAds.length === 0 && folders.length === 0;
    // Nothing left after filtering is not the same as an empty collection: one needs the
    // filter relaxed, the other needs content adding.
    const isFilteredEmpty = !isEmpty && filtering && visibleCount === 0;

    return (
        <div className="moti-store">
            <Link to={`${import.meta.env.BASE_URL}${Paths.MOTI_STORE}`} className="moti-store__back">
                <ArrowLeft size={14} /> Back to Store
            </Link>

            <div className="moti-store__title">{title || "Motimatic Store"}</div>

            <StoreHero src={heroSrc} isLoaded={isLoaded} />

            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 16,
                marginBottom: 24,
            }}>
                <div style={{ color: "#6b7280", fontSize: 13, flex: 1 }}>
                    {copyText ? stripHtmlTags(copyText) : ""}
                </div>
                <Button
                    variant="contained"
                    onClick={() => setSelectorOpen(true)}
                    disabled={isEmpty}
                >
                    Request Content
                </Button>
            </div>

            {!isEmpty && (
                <StoreFilterBar
                    tagGroups={tagGroups}
                    selectedTagIds={selectedTagIds}
                    onToggleTag={toggleTag}
                    onClearTags={() => setSelectedTagIds([])}
                    query={query}
                    onQueryChange={setQuery}
                    resultCount={visibleCount}
                    isFiltering={filtering}
                />
            )}

            {isEmpty ? (
                <div className="moti-store__empty">No content in this collection.</div>
            ) : isFilteredEmpty ? (
                <div className="moti-store__empty">No GuidePosts match the current filters.</div>
            ) : (
                <>
                    {visibleRootAds.length > 0 && (
                        <div className="moti-store__ad-grid">
                            {visibleRootAds.map((ad) => (
                                <AdCard key={ad.id} ad={ad} mode="detail" menuActions={[]} hideType hideMenu />
                            ))}
                        </div>
                    )}
                    {visibleFolders.map((folder) => <FolderAccordion key={folder.id} folder={folder} />)}
                </>
            )}

            <CollectionSelector open={selectorOpen} setIsOpen={setSelectorOpen} folders={selectorFolders} />
        </div>
    );
}
